import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import {
    ActivityHistorySystemTags,
    ActivityHistoryTagsMap,
} from '@evidentid/tprm-portal-lib/models/activity-history';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

export interface EntityTagsRequirement {
    gatewayClient: GatewayApiClient;
}

interface EntityTagsState {
    tagsStatus: Record<string, { status: OperationStatus, tags: ActivityHistoryTagsMap }>;
    systemTagsStatus: { status: OperationStatus, tags: ActivityHistorySystemTags };
}

// Every bucket is listed even though the map type does not require it, so a lookup
// for any resource type finds an empty list rather than undefined.
function emptyTags(): ActivityHistoryTagsMap {
    return {
        VERIFICATION: [],
        NOTE: [],
        DOCUMENT: [],
        EVAL_OVERRIDE: [],
        INSURED: [],
        RISK_PROFILE: [],
        ACTION: [],
        DOCUMENT_DATA_REVIEW: [],
    };
}

function emptySystemTags(): ActivityHistorySystemTags {
    return {
        VERIFICATION: [],
        NOTE: [],
        DOCUMENT: [],
        EVAL_OVERRIDE: [],
        INSURED: [],
        RISK_PROFILE: [],
        ACTION: [],
        DOCUMENT_DATA_REVIEW: [],
    };
}

export const useEntityTagsStore = defineStore('entityTags', {
    state: (): EntityTagsState => ({
        tagsStatus: {},
        systemTagsStatus: { status: OperationStatus.uninitialized, tags: emptySystemTags() },
    }),
    actions: {
        async loadTags(payload: { rpName: string }) {
            const { rpName } = payload;
            if (this.tagsStatus[rpName]?.status === OperationStatus.loading) {
                return;
            }
            const { gatewayClient } = this.$app as EntityTagsRequirement;
            try {
                this.tagsStatus = {
                    ...this.tagsStatus,
                    [rpName]: {
                        status: OperationStatus.loading,
                        tags: this.tagsStatus[rpName]?.tags || emptyTags(),
                    },
                };
                const tags = await gatewayClient.tprmUi().getActivityHistoryTags(rpName);
                this.tagsStatus = {
                    ...this.tagsStatus,
                    [rpName]: {
                        status: OperationStatus.success,
                        tags,
                    },
                };
            } catch (error) {
                console.warn('load activity history tags error', error);
                this.tagsStatus = {
                    ...this.tagsStatus,
                    [rpName]: {
                        status: OperationStatus.error,
                        tags: this.tagsStatus[rpName]?.tags || emptyTags(),
                    },
                };
            }
        },
        // System tags are RP-independent, so this loads once per session: an
        // in-flight or already-successful load short-circuits, while a failure
        // leaves the status on `error` so the next call retries.
        async loadSystemTags() {
            const { status } = this.systemTagsStatus;
            if (status === OperationStatus.loading || status === OperationStatus.success) {
                return;
            }
            const { gatewayClient } = this.$app as EntityTagsRequirement;
            try {
                this.systemTagsStatus = {
                    status: OperationStatus.loading,
                    tags: this.systemTagsStatus.tags,
                };
                const systemTags = await gatewayClient.tprmUi().getSystemTags();
                this.systemTagsStatus = { status: OperationStatus.success, tags: systemTags };
            } catch (error) {
                console.warn('load system tags error', error);
                this.systemTagsStatus = {
                    status: OperationStatus.error,
                    tags: this.systemTagsStatus.tags,
                };
            }
        },
    },
});
